import { useState } from "react";
import { notifications } from "@mantine/notifications";
import api from "../lib/apiClient";
import type { FormDraft } from "./useFormDrafts";

/**
 * US-115 follow-up: fetches the full draft (with `data`) by id — the list
 * endpoint omits it to keep the card list lean (see useFormDrafts). Shared by
 * every "resume a draft" entry point (facility/staff forms; the resident flow
 * has its own longer-lived DraftFormMenu/ResidentFormFillModal pairing that
 * predates this hook and wasn't migrated to avoid touching stable, already
 * reviewed code for no functional gain).
 */
export function useDraftResume() {
  const [resumingDraftId, setResumingDraftId] = useState<string | null>(null);

  const resumeDraft = async (draftId: string): Promise<FormDraft | null> => {
    setResumingDraftId(draftId);
    try {
      const { data } = await api.get(`/form-submissions/drafts/${draftId}`);
      return data.data as FormDraft;
    } catch {
      notifications.show({
        title: "Couldn't open draft",
        message: "It may have been submitted or removed. Please refresh.",
        color: "red",
      });
      return null;
    } finally {
      setResumingDraftId(null);
    }
  };

  return { resumingDraftId, resumeDraft };
}
